/**
 * Customer Form Handler
 * Extracted JavaScript logic from Checkout_Template.html
 * Now uses aspnetForm directly - no nested forms
 */

import { getStorage, clearStorage } from '../core/storage.js';
import { BamboraProvider } from './providers/bambora.js';
import { NoneProvider } from './providers/none.js';
import { t } from '../core/i18n.js';

export class CustomerFormHandler {
    constructor(system) {
        this.system = system;
        this.initialized = false;
    }

    init() {
        if (this.initialized) return;
        
        console.log('Initializing checkout...');
        
        // Wait for ECommerceSystem to load
        this.waitForEcommerce();
    }

    waitForEcommerce() {
        if (typeof window.ecommerce !== 'undefined' && window.ecommerce) {
            console.log('ECommerceSystem found, setting up checkout...');
            this.setupCheckout();
        } else {
            console.log('CustomerFormHandler: Waiting for ecommerceReady event...');
            // Listen for ecommerceReady event instead of polling
            document.addEventListener('ecommerceReady', () => {
                console.log('CustomerFormHandler: ecommerceReady event received, setting up checkout...');
                this.setupCheckout();
            }, { once: true });
        }
    }

    setupCheckout() {
        const provider = this.getPaymentProviderType();
        console.log('Setting up checkout for provider:', provider);
        
        // Debug: Check which sections are rendered
        const klarnaSection = document.getElementById('klarna');
        const aspnetForm = document.getElementById('aspnetForm');
        const placeOrderBtn = document.getElementById('place-order-btn');
        
        console.log('Klarna section found:', !!klarnaSection);
        console.log('ASP.NET form found:', !!aspnetForm);
        console.log('Place order button found:', !!placeOrderBtn);
        
        // Mark form as initialized to prevent duplicate initialization
        if (aspnetForm) {
            aspnetForm.dataset.initialized = 'true';
        }
        
        // Populate order summary
        this.populateOrderSummary();
        
        // Setup form interactions
        this.setupFormInteractions();
        
        // Setup payment button
        this.setupPaymentButton(provider);
        
        // Listen for shipping changes to update order summary
        document.addEventListener('shippingOptionChanged', () => {
            console.log('CustomerFormHandler: Shipping option changed, re-populating order summary');
            this.populateOrderSummary();
        });
        
        this.initialized = true;
    }

    getPaymentProviderType() {
        // Try to get from template tokens first
        const providerElements = document.querySelectorAll('[data-payment-provider]');
        if (providerElements.length > 0) {
            return providerElements[0].dataset.paymentProvider;
        }
        
        // Fallback to system config
        if (window.ecommerce && window.ecommerce.config && window.ecommerce.config.checkout) {
            return window.ecommerce.config.checkout.provider;
        }
        
        return 'bambora'; // Default fallback
    }

    populateOrderSummary() {
        console.log('🔥 CustomerFormHandler: populateOrderSummary() CALLED! 🔥');
        
        // Debug: Check system state
        console.log('=== POPULATE ORDER SUMMARY DEBUG ===');
        console.log('this.system:', this.system);
        console.log('this.system?.config:', this.system?.config);
        console.log('this.system?.config?.storageKey:', this.system?.config?.storageKey);
        
        // Use default storage key if system is not ready
        const storageKey = this.system?.config?.storageKey || 'cartItems';
        console.log('Using storage key:', storageKey);
        
        const cartItems = getStorage(storageKey);
        console.log('Cart items from storage:', cartItems);
        console.log('Cart items length:', cartItems.length);
        
        // Additional debug: Test direct sessionStorage access
        console.log('Direct sessionStorage test:');
        console.log('sessionStorage.getItem("cartItems"):', sessionStorage.getItem('cartItems'));
        try {
            const directItems = JSON.parse(sessionStorage.getItem('cartItems') || '[]');
            console.log('Direct parsed items:', directItems);
            console.log('Direct items length:', directItems.length);
        } catch (e) {
            console.error('Failed to parse direct sessionStorage:', e);
        }
        
        const orderItemsList = document.getElementById('order-items-list');
        const subtotalElement = document.getElementById('order-subtotal-amount');
        const totalElement = document.getElementById('order-total-amount');
        
        console.log('Order items list element:', orderItemsList);
        console.log('Subtotal element:', subtotalElement);
        console.log('Total element:', totalElement);
        
        if (!orderItemsList) {
            console.error('CRITICAL: order-items-list element not found!');
            return;
        }

        // An empty cart (e.g. after the inline cart emptied it and the page
        // reloaded) must show a clear empty state with a disabled order button
        const placeOrderBtn = document.getElementById('place-order-btn');
        if (placeOrderBtn) {
            placeOrderBtn.disabled = !cartItems.length;
        }

        if (!cartItems.length) {
            console.log('No cart items found - showing empty cart message');
            orderItemsList.innerHTML = `<div class="empty-cart">${t('checkout.emptyCart')}</div>`;
            if (subtotalElement) subtotalElement.textContent = '0 kr';
            if (totalElement) totalElement.textContent = '0 kr';
            return;
        }
        
        // Detect if prices include VAT (B2C) or exclude VAT (B2B)
        const pricesIncludeVat = this.system?.config?.pricesIncludeVat ?? true;
        console.log('PricesIncludeVat setting:', pricesIncludeVat);
        
        let subtotal = 0;
        let subtotalInclVat = 0;
        let totalVat = 0;
        let html = '';
        
        cartItems.forEach(item => {
            const itemTotal = parseFloat(item.ProductPrice) * item.OrderCount;
            const taxRate = item.TaxRate || 25; // Default to 25% if not specified
            
            subtotal += itemTotal;
            
            // Calculate total including VAT and VAT amount
            let itemTotalInclVat, itemVat;
            if (pricesIncludeVat) {
                // B2C: Price already includes VAT - extract VAT
                itemTotalInclVat = itemTotal;
                itemVat = itemTotal * (taxRate / (100 + taxRate));
            } else {
                // B2B: Price excludes VAT - add VAT
                itemVat = itemTotal * (taxRate / 100);
                itemTotalInclVat = itemTotal + itemVat;
            }
            
            subtotalInclVat += itemTotalInclVat;
            totalVat += itemVat;
            
            // Gift card info (KISS - compact display)
            const giftCardInfo = item.isGiftCard && item.giftCardData ? `
                <div class="gift-card-details">
                    <small><strong>${t('cart.giftCard.to')}</strong> ${item.giftCardData.recipientName}${item.giftCardData.recipientEmail ? ` (${item.giftCardData.recipientEmail})` : ''}</small><br>
                    <small><strong>${t('cart.giftCard.from')}</strong> ${item.giftCardData.senderName}</small>
                    ${item.giftCardData.giftMessage ? `<br><small><strong>${t('cart.giftCard.message')}</strong> ${item.giftCardData.giftMessage}</small>` : ''}
                        ${item.giftCardData.isPhysicalCard ? `
                            <br><small><strong>${t('cart.giftCard.physical')}</strong> ${t('cart.giftCard.yes')} (+${item.giftCardData.physicalCardPrice} kr)</small>
                            ${item.giftCardData.deliveryAddress ? `
                                <br><small><strong>${t('cart.giftCard.deliveryAddress')}</strong> 
                                ${item.giftCardData.deliveryAddress.deliveryName} ${item.giftCardData.deliveryAddress.deliveryLastname}, 
                                ${item.giftCardData.deliveryAddress.deliveryAddress}, 
                                ${item.giftCardData.deliveryAddress.deliveryZip} ${item.giftCardData.deliveryAddress.deliveryCity}</small>
                            ` : ''}
                        ` : ''}
                </div>
            ` : '';
            
            html += `
                <div class="order-item">
                    <div class="item-info">
                        <span class="item-name">${item.ProductName}</span>
                        ${giftCardInfo}
                        <span class="item-quantity">× ${item.OrderCount}</span>
                    </div>
                    <span class="item-total">${itemTotal.toFixed(2)} kr</span>
                </div>
            `;
        });
        
        orderItemsList.innerHTML = html;
        
        // Get B2B breakdown elements
        const exclVatLabel = document.getElementById('order-excl-vat-label');
        const inclVatLabel = document.getElementById('order-incl-vat-label');
        const subtotalExclVatRow = document.getElementById('order-subtotal-excl-vat-row');
        const subtotalExclVatAmount = document.getElementById('order-subtotal-excl-vat-amount');
        const vatRow = document.getElementById('order-vat-row');
        const vatAmount = document.getElementById('order-vat-amount');
        const vatLabel = document.getElementById('order-vat-label');
        
        // Display subtotal and total based on pricing mode
        if (pricesIncludeVat) {
            // B2C: Show total as-is (includes VAT)
            if (subtotalElement) subtotalElement.textContent = subtotal.toFixed(2) + ' kr';
            if (totalElement) totalElement.textContent = subtotal.toFixed(2) + ' kr';
            
            // Hide B2B-specific elements
            if (exclVatLabel) exclVatLabel.style.display = 'none';
            if (inclVatLabel) inclVatLabel.style.display = 'none';
            if (subtotalExclVatRow) subtotalExclVatRow.style.display = 'none';
            if (vatRow) vatRow.style.display = 'none';
            
            console.log('Order summary (B2C) populated with', cartItems.length, 'items, total incl VAT:', subtotal.toFixed(2), 'kr');
        } else {
            // B2B: Show prices excl VAT + VAT breakdown
            // IMPORTANT: In B2B mode, VAT must be calculated on products + shipping
            
            // Get shipping cost from ShippingOptionsManager
            let shippingCostExclVat = 0;
            let shippingTaxRate = 25; // Default shipping tax rate
            
            if (this.system?.shippingOptions) {
                shippingCostExclVat = this.system.shippingOptions.getShippingCost() || 0;
                // Get shipping tax rate if available
                const shippingData = this.system.shippingOptions.getShippingData();
                if (shippingData && shippingData.shippingTaxRate) {
                    shippingTaxRate = shippingData.shippingTaxRate;
                }
            }
            
            console.log('B2B VAT calculation - shipping cost excl VAT:', shippingCostExclVat, 'kr, shipping tax rate:', shippingTaxRate + '%');
            
            // Calculate subtotal excl VAT (products + shipping)
            const subtotalExclVatWithShipping = subtotal + shippingCostExclVat;
            
            // Calculate VAT on products
            const firstTaxRate = cartItems[0]?.TaxRate || 25;
            const productVat = subtotal * (firstTaxRate / 100);
            
            // Calculate VAT on shipping
            const shippingVat = shippingCostExclVat * (shippingTaxRate / 100);
            
            // Total VAT (products + shipping)
            const totalVatWithShipping = productVat + shippingVat;
            
            // Total incl VAT (products + shipping + VAT)
            const totalInclVatWithShipping = subtotalExclVatWithShipping + totalVatWithShipping;
            
            // Display subtotal (products only, excl VAT)
            if (subtotalElement) subtotalElement.textContent = subtotal.toFixed(2) + ' kr';
            
            // Display total incl VAT (products + shipping + VAT)
            if (totalElement) totalElement.textContent = totalInclVatWithShipping.toFixed(2) + ' kr';
            
            // Show B2B-specific labels and rows
            if (exclVatLabel) exclVatLabel.style.display = 'inline';
            if (inclVatLabel) inclVatLabel.style.display = 'inline';
            
            // Show VAT breakdown
            if (vatRow) {
                vatRow.style.display = 'flex';
                if (vatAmount) vatAmount.textContent = totalVatWithShipping.toFixed(2) + ' kr';
                // Update VAT label with actual rate
                if (vatLabel) vatLabel.textContent = `Moms (${firstTaxRate}%)`;
            }
            
            console.log('Order summary (B2B) populated with', cartItems.length, 'items:');
            console.log('  - Products excl VAT:', subtotal.toFixed(2), 'kr');
            console.log('  - Shipping excl VAT:', shippingCostExclVat.toFixed(2), 'kr');
            console.log('  - Subtotal excl VAT:', subtotalExclVatWithShipping.toFixed(2), 'kr');
            console.log('  - Product VAT ('+firstTaxRate+'%):', productVat.toFixed(2), 'kr');
            console.log('  - Shipping VAT ('+shippingTaxRate+'%):', shippingVat.toFixed(2), 'kr');
            console.log('  - Total VAT:', totalVatWithShipping.toFixed(2), 'kr');
            console.log('  - Total incl VAT:', totalInclVatWithShipping.toFixed(2), 'kr');
        }

        // Gift card payment (Phase 3): this method just rewrote the displayed
        // total WITHOUT the deduction. When a card is applied, let the central
        // pipeline rewrite it with the deduction applied last (B10).
        const giftCardPaymentManager = window.ecommerce?.checkout?.giftCardPaymentManager;
        if (giftCardPaymentManager?.isApplied?.()) {
            giftCardPaymentManager.requestTotalUpdate();
        }
    }

    setupFormInteractions() {
        // Delivery address toggle
        const deliveryCheckbox = document.getElementById('differentDeliveryAddress');
        const deliveryFields = document.getElementById('delivery-address-fields');
        
        if (deliveryCheckbox && deliveryFields) {
            deliveryCheckbox.addEventListener('change', function() {
                deliveryFields.style.display = this.checked ? 'block' : 'none';
            });
        }
        
        // Form validation on field changes - use aspnetForm
        const form = document.getElementById('aspnetForm');
        if (form) {
            const requiredFields = form.querySelectorAll('input[required], select[required]');
            // Filter out ASP.NET system fields
            const customerFields = Array.from(requiredFields).filter(field => {
                const fieldName = field.name || field.id || '';
                return !fieldName.startsWith('__');
            });
            
            customerFields.forEach(field => {
                field.addEventListener('blur', () => {
                    this.validateField(field);
                });
                
                field.addEventListener('input', () => {
                    if (field.classList.contains('error')) {
                        this.validateField(field);
                    }
                });
            });
        }
    }

    setupPaymentButton(provider) {
        const placeOrderBtn = document.getElementById('place-order-btn');
        if (!placeOrderBtn) return;
        
        placeOrderBtn.addEventListener('click', () => {
            console.log('Place Order clicked, provider:', provider);
            const isValid = this.validateAllFields();
            console.log('Form validation result:', isValid);
            if (isValid) {
                if (provider === 'klarna' || provider === '1' || provider === 1) {
                    this.processKlarnaPayment();
                } else if (provider === 'bambora' || provider === '2' || provider === 2) {
                    this.processBamboraPayment();
                } else if (provider === 'stripe' || provider === '3' || provider === 3) {
                    this.processStripePayment();
                } else if (provider === 'none' || provider === '0' || provider === 0) {
                    this.processTestPayment();
                } else {
                    console.log('Unknown provider type:', provider);
                }
            } else {
                console.log('Form validation failed - not proceeding with payment');
            }
        });
    }

    validateField(field) {
        const isValid = field.value.trim() !== '';
        field.classList.toggle('error', !isValid);
        return isValid;
    }

    validateAllFields() {
        console.log('Starting form validation...');
        
        // Use ASP.NET form wrapper as primary, with fallbacks
        const form = document.getElementById('aspnetForm') || 
                    document.querySelector('.customer-form') || 
                    document.querySelector('form');
        const termsCheckbox = document.getElementById('acceptTerms');
        
        // Debug: Check what forms exist on the page
        const allForms = document.querySelectorAll('form');
        console.log('All forms found on page:', allForms.length);
        allForms.forEach((f, index) => {
            console.log(`Form ${index}: id="${f.id || 'no-id'}", class="${f.className || 'no-class'}"`);
        });
        
        console.log('Selected form for validation:', form ? `id="${form.id}", class="${form.className}"` : 'NONE FOUND');
        
        if (!form) {
            console.error('No form found for validation (tried aspnetForm, .customer-form, and generic form)');
            return false;
        }
        
        return this.validateFormFields(form, termsCheckbox);
    }

    validateFormFields(form, termsCheckbox) {
        let isValid = true;
        
        // Filter out ASP.NET system fields from validation
        const allRequiredFields = form.querySelectorAll('input[required], select[required]');
        const requiredFields = Array.from(allRequiredFields).filter(field => {
            const fieldName = field.name || field.id || '';
            const isAspNetField = fieldName.startsWith('__');
            return !isAspNetField;
        });
        
        console.log('Found', allRequiredFields.length, 'total required fields,', requiredFields.length, 'customer data fields (filtered out ASP.NET system fields)');
        
        // Clear previous errors
        form.querySelectorAll('.error').forEach(el => el.classList.remove('error'));
        if (termsCheckbox) termsCheckbox.classList.remove('error');
        
        // Validate customer data fields only
        requiredFields.forEach(field => {
            const fieldValue = field.value.trim();
            const fieldName = field.name || field.id;
            console.log('Validating field:', fieldName, 'value:', fieldValue, 'isEmpty:', !fieldValue);
            
            if (!fieldValue) {
                console.log('Field failed validation:', fieldName);
                field.classList.add('error');
                isValid = false;
            }
        });
        
        // Validate delivery address if selected
        const deliveryCheckbox = document.getElementById('differentDeliveryAddress');
        if (deliveryCheckbox && deliveryCheckbox.checked) {
            console.log('Delivery address is selected, validating delivery fields...');
            const deliveryRequiredFields = ['deliveryName', 'deliveryLastname', 'deliveryAddress', 'deliveryZip', 'deliveryCity'];
            deliveryRequiredFields.forEach(fieldName => {
                const field = document.querySelector(`[name="${fieldName}"]`);
                if (field && !field.value.trim()) {
                    console.log('Delivery field failed validation:', fieldName);
                    field.classList.add('error');
                    isValid = false;
                }
            });
        }
        
        // Validate terms acceptance
        console.log('Terms checkbox found:', !!termsCheckbox);
        if (termsCheckbox) {
            console.log('Terms checkbox checked:', termsCheckbox.checked);
            if (!termsCheckbox.checked) {
                console.log('Terms checkbox not checked - validation failed');
                termsCheckbox.classList.add('error');
                isValid = false;
            }
        }
        
        console.log('Final validation result:', isValid);
        
        if (!isValid) {
            this.showError(t('checkout.fillRequired'));
            
            // Scroll to first error
            const firstError = form.querySelector('.error') || termsCheckbox;
            if (firstError) {
                firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
                if (firstError.focus) firstError.focus();
            }
        }
        
        return isValid;
    }

    validateCartStock() {
        const cartItems = getStorage(this.system?.config?.storageKey || 'cartItems');
        const errors = [];
        
        for (const item of cartItems) {
            const trackInventory = item.EffectiveTrackInventory === true || item.EffectiveTrackInventory === 'True';
            const allowBackorders = item.EffectiveAllowBackorders === true || item.EffectiveAllowBackorders === 'True';
            
            if (trackInventory && !allowBackorders) {
                const maxStock = parseInt(item.EffectiveStockQuantity || '0');
                if (item.OrderCount > maxStock) {
                    errors.push(`${item.ProductName}: Endast ${maxStock} st (du har ${item.OrderCount} st i varukorgen)`);
                }
            }
        }
        
        return errors;
    }

    async processKlarnaPayment() {
        console.log('Processing Klarna payment...');
        this.setButtonLoading(true);
        
        try {
            // Validate stock before processing payment
            const stockErrors = this.validateCartStock();
            if (stockErrors.length > 0) {
                throw new Error('Lagerproblem:\n' + stockErrors.join('\n'));
            }
            
            const cartItems = getStorage(this.system?.config?.storageKey || 'cartItems');
            if (!cartItems.length) {
                throw new Error('No items in cart');
            }
            
            // Get customer data from form
            const customerFormData = this.getCustomerFormData();
            
            // Build order data using KlarnaProvider helper
            const config = window.ecommerce?.config?.checkout || {};
            const KlarnaProvider = (await import('./providers/klarna.js')).KlarnaProvider;
            const orderData = KlarnaProvider.buildOrderData(customerFormData, cartItems, config);
            
            console.log('Calling Klarna API with order data:', orderData);
            
            // Call API to create Klarna checkout
            const result = await KlarnaProvider.initiatePayment(orderData);
            
            console.log('Klarna API response:', result);
            
            if (result.htmlSnippet) {
                console.log('Rendering Klarna checkout widget...');
                // Render Klarna widget in container
                const klarnaContainer = document.getElementById('klarna') || 
                                      document.getElementById('checkout-container');
                                      
                if (klarnaContainer) {
                    klarnaContainer.innerHTML = result.htmlSnippet;
                    
                    // Execute scripts in the Klarna snippet
                    const scripts = klarnaContainer.getElementsByTagName('script');
                    for (let i = 0; i < scripts.length; i++) {
                        const script = scripts[i];
                        const newScript = document.createElement('script');
                        newScript.type = 'text/javascript';
                        if (script.src) {
                            newScript.src = script.src;
                        } else {
                            newScript.text = script.text;
                        }
                        script.parentNode.replaceChild(newScript, script);
                    }
                    
                    // Hide the form and show Klarna widget
                    const checkoutLayout = document.querySelector('.checkout-layout');
                    if (checkoutLayout) {
                        checkoutLayout.style.display = 'none';
                        console.log("Hidden checkout form layout to show Klarna widget");
                    }
                    
                    console.log('Klarna widget rendered successfully');
                } else {
                    throw new Error('No container found for Klarna widget');
                }
            } else {
                throw new Error('No HTML snippet received from Klarna API');
            }
            
        } catch (error) {
            console.error('Failed to process Klarna payment:', error);
            this.showError(t('checkout.paymentError'));
            this.setButtonLoading(false);
        }
    }

    async processBamboraPayment() {
        console.log('Processing Bambora payment...');
        this.setButtonLoading(true);
        
        try {
            // Validate stock before processing payment
            const stockErrors = this.validateCartStock();
            if (stockErrors.length > 0) {
                throw new Error('Lagerproblem:\n' + stockErrors.join('\n'));
            }
            
            const cartItems = getStorage(this.system?.config?.storageKey || 'cartItems');
            if (!cartItems.length) {
                throw new Error('No items in cart');
            }
            
            // Get customer data from form
            const customerFormData = this.getCustomerFormData();
            
            // Calculate total amount in öre
            const totalAmount = cartItems.reduce((sum, item) => {
                return sum + (parseFloat(item.ProductPrice) * item.OrderCount * 100);
            }, 0);
            
            // Build order data using BamboraProvider helper
            const config = window.ecommerce?.config?.checkout || {};

            // Phase 5: a gift card covering the ENTIRE order completes via
            // /payments/initiate/none instead - no Bambora transaction is
            // created. The server recomputes the amounts (condition (b)).
            const giftCardPaymentManager = window.ecommerce?.checkout?.giftCardPaymentManager;
            if (giftCardPaymentManager?.isApplied?.() && giftCardPaymentManager.isFullCoverage()) {
                console.log('Gift card covers the entire order - routing to /payments/initiate/none (no Bambora payment is made)');
                await this.processGiftCardCoveredOrder(customerFormData, cartItems);
                return;
            }

            const orderData = BamboraProvider.buildOrderData(customerFormData, cartItems, config);
            
            console.log('Calling Bambora API with order data:', orderData);
            
            // Call API to get Bambora checkout URL
            const response = await fetch('/cart/api/v1/payments/initiate/bambora', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(orderData)
            });
            
            if (!response.ok) {
                const errorText = await response.text();
                console.error('Bambora API error response:', errorText);
                throw new Error(`HTTP error! status: ${response.status} - ${errorText}`);
            }
            
            const result = await response.json();
            console.log('Bambora API response:', result);
            
            if (result.checkoutUrl) {
                console.log('Redirecting to Bambora checkout:', result.checkoutUrl);
                // NOTE: Cart is NOT cleared here - only cleared on successful payment completion
                // This allows customer to return to checkout if they cancel the payment
                // Redirect to Bambora checkout
                window.location.href = result.checkoutUrl;
            } else {
                throw new Error('No checkout URL received from Bambora API');
            }
            
        } catch (error) {
            console.error('Failed to process Bambora payment:', error);
            this.showError(t('checkout.paymentError'));
            this.setButtonLoading(false);
        }
    }

    async processStripePayment() {
        console.log('Processing Stripe payment...');
        this.setButtonLoading(true);
        
        try {
            // Validate stock before processing payment
            const stockErrors = this.validateCartStock();
            if (stockErrors.length > 0) {
                throw new Error('Lagerproblem:\n' + stockErrors.join('\n'));
            }
            
            const cartItems = getStorage(this.system?.config?.storageKey || 'cartItems');
            if (!cartItems.length) {
                throw new Error('No items in cart');
            }
            
            // Get customer data from form
            const customerFormData = this.getCustomerFormData();
            
            // Build order data using StripeProvider helper
            const config = window.ecommerce?.config?.checkout || {};

            // Phase 5: a gift card covering the ENTIRE order completes via
            // /payments/initiate/none instead - no Stripe session or coupon
            // is created. The server recomputes the amounts (condition (b)).
            const giftCardPaymentManager = window.ecommerce?.checkout?.giftCardPaymentManager;
            if (giftCardPaymentManager?.isApplied?.() && giftCardPaymentManager.isFullCoverage()) {
                console.log('Gift card covers the entire order - routing to /payments/initiate/none (no Stripe payment is made)');
                await this.processGiftCardCoveredOrder(customerFormData, cartItems);
                return;
            }

            const StripeProvider = (await import('./providers/stripe.js')).StripeProvider;
            const orderData = StripeProvider.buildOrderData(customerFormData, cartItems, config);
            
            console.log('Calling Stripe API with order data:', orderData);
            
            // Call API to get Stripe checkout URL
            const result = await StripeProvider.initiatePayment(orderData);
            
            console.log('Stripe API response:', result);
            
            if (result.checkoutUrl) {
                console.log('Redirecting to Stripe checkout:', result.checkoutUrl);
                // NOTE: Cart is NOT cleared here - only cleared on successful payment completion
                // This allows customer to return to checkout if they cancel the payment
                // Redirect to Stripe checkout
                window.location.href = result.checkoutUrl;
            } else {
                throw new Error('No checkout URL received from Stripe API');
            }
            
        } catch (error) {
            console.error('Failed to process Stripe payment:', error);
            this.showError(t('checkout.paymentError'));
            this.setButtonLoading(false);
        }
    }

    /**
     * Phase 5: completes an order fully covered by an applied gift card via
     * /payments/initiate/none - NO payment provider is called (no Stripe
     * session or coupon, no Bambora transaction). The server recomputes the
     * amounts and only allows the anonymous None order when the remainder is
     * exactly 0 (condition (b)); the gift card is redeemed exactly once in
     * the None completion flow. Used by the Stripe/Bambora submit paths when
     * the gift card covers the entire order, and by the None provider path.
     */
    async processGiftCardCoveredOrder(customerFormData, cartItems) {
        console.log('Processing gift card covered order via /payments/initiate/none (no payment provider call)...');
        const config = window.ecommerce?.config?.checkout || {};
        const orderData = NoneProvider.buildInitiateOrderData(customerFormData, cartItems, config);
        const result = await NoneProvider.initiatePayment(orderData);

        if (result.status === 'completed' && result.orderReference) {
            clearStorage(this.system?.config?.storageKey || 'cartItems');
            const confirmUrl = config.confirmUrl || window.location.origin + '/thank-you/';
            window.location.href = `${confirmUrl}?orderId=${result.orderReference}&status=success`;
        } else {
            throw new Error('Gift card order was not completed');
        }
    }

    async processTestPayment() {
        console.log('Processing direct order (none provider)...');
        this.setButtonLoading(true);
        
        try {
            // Validate stock before processing payment
            const stockErrors = this.validateCartStock();
            if (stockErrors.length > 0) {
                throw new Error('Lagerproblem:\n' + stockErrors.join('\n'));
            }
            
            const cartItems = getStorage(this.system?.config?.storageKey || 'cartItems');
            if (!cartItems.length) {
                throw new Error('No items in cart');
            }
            
            // Get customer data from form
            const customerFormData = this.getCustomerFormData();
            
            // Phase 3/5: with an applied gift card the order must go through
            // /payments/initiate/none - /order/direct has no gift card support
            // and only a fully covered order is allowed (server condition (b)).
            // A partially covered order is already blocked in the UI by
            // GiftCardPaymentManager (button disabled).
            const giftCardPaymentManager = window.ecommerce?.checkout?.giftCardPaymentManager;
            if (giftCardPaymentManager?.isApplied?.()) {
                await this.processGiftCardCoveredOrder(customerFormData, cartItems);
                return;
            }

            // Build order data using NoneProvider helper
            const config = window.ecommerce?.config?.checkout || {};
            const orderData = NoneProvider.buildOrderData(customerFormData, cartItems, config);
            
            console.log('Calling direct order API with order data:', orderData);
            
            // Call API to create direct order
            const result = await NoneProvider.processDirectOrder(orderData);
            
            console.log('Direct order API response:', result);
            
            if (result.orderReference) {
                console.log('Order created successfully with reference:', result.orderReference);
                
                // Clear cart and redirect to success
                clearStorage(this.system?.config?.storageKey || 'cartItems');
                
                // Get confirmUrl from config
                const confirmUrl = config.confirmUrl || window.location.origin + '/thank-you/';
                const redirectUrl = `${confirmUrl}?orderId=${result.orderReference}&status=success`;
                
                console.log('Redirecting to:', redirectUrl);
                window.location.href = redirectUrl;
            } else {
                throw new Error('No order reference received from direct order API');
            }
            
        } catch (error) {
            console.error('Failed to process direct order:', error);
            this.showError(error.message || t('checkout.paymentError'));
            this.setButtonLoading(false);
        }
    }

    getCustomerFormData() {
        console.log('=== COLLECTING CUSTOMER FORM DATA ===');
        
        // Use ASP.NET form wrapper as primary, with fallbacks
        const form = document.getElementById('aspnetForm') || 
                    document.querySelector('.customer-form') || 
                    document.querySelector('form');
        
        if (!form) {
            console.error('CRITICAL: No form found!');
            console.log('Available forms on page:');
            document.querySelectorAll('form').forEach((f, i) => {
                console.log(`Form ${i}: id="${f.id || 'no-id'}", class="${f.className || 'no-class'}"`);
            });
            return {};
        }
        
        console.log('Form found successfully:', form ? `id="${form.id}", class="${form.className}"` : 'NONE');
        
        // ASP.NET system fields to ignore
        const aspNetSystemFields = [
            '__VIEWSTATE',
            '__VIEWSTATEGENERATOR', 
            '__EVENTVALIDATION',
            '__EVENTTARGET',
            '__EVENTARGUMENT',
            '__LASTFOCUS',
            '__DOPOSTBACK'
        ];
        
        // Debug: Log all form fields and their current values
        console.log('=== FORM FIELD ANALYSIS ===');
        const allInputs = form.querySelectorAll('input, select, textarea');
        console.log(`Total form elements found: ${allInputs.length}`);
        
        allInputs.forEach((input, index) => {
            const name = input.name || 'NO_NAME';
            const id = input.id || 'NO_ID';
            const isAspNetField = aspNetSystemFields.includes(name) || aspNetSystemFields.includes(id) || name.startsWith('__') || id.startsWith('__');
            const value = isAspNetField ? '[ASP.NET SYSTEM FIELD]' : (input.value || '');
            const checked = input.type === 'checkbox' ? input.checked : 'N/A';
            console.log(`[${index}] ${input.tagName} - name:"${name}" id:"${id}" value:"${value}" checked:${checked} type:"${input.type}" aspnet:${isAspNetField}`);
        });
        
        // Initialize customer data object
        const customerData = {};
        
        // Method 1: Direct DOM queries (most reliable)
        console.log('=== DIRECT DOM COLLECTION (PRIMARY) ===');
        const fieldMappings = {
            'name': 'customerName',
            'lastname': 'customerLastname', 
            'companyName': 'companyName',
            'country': 'customerCountry',
            'address': 'customerAddress',
            'address2': 'customerAddress2',
            'zip': 'customerZip',
            'city': 'customerCity',
            'phone': 'customerPhone',
            'email': 'customerEmail',
            'acceptTerms': 'acceptTerms',
            'differentDeliveryAddress': 'differentDeliveryAddress'
        };
        
        Object.entries(fieldMappings).forEach(([fieldName, elementId]) => {
            const element = document.getElementById(elementId);
            if (element) {
                let value;
                if (element.type === 'checkbox') {
                    value = element.checked;
                } else {
                    value = element.value ? element.value.trim() : '';
                }
                customerData[fieldName] = value;
                console.log(`✓ ${fieldName}: "${value}" (from #${elementId})`);
            } else {
                console.error(`✗ Element #${elementId} not found for field ${fieldName}`);
                customerData[fieldName] = '';
            }
        });
        
        // Method 2: FormData as verification (secondary) - with ASP.NET filtering
        console.log('=== FORMDATA VERIFICATION (SECONDARY) ===');
        try {
            const formData = new FormData(form);
            let formDataCount = 0;
            let aspNetFieldCount = 0;
            
            for (let [key, value] of formData.entries()) {
                const isAspNetField = aspNetSystemFields.includes(key) || key.startsWith('__');
                
                if (isAspNetField) {
                    aspNetFieldCount++;
                    console.log(`FormData (ASP.NET): ${key} = [SYSTEM FIELD]`);
                } else {
                    formDataCount++;
                    console.log(`FormData: ${key} = "${value}"`);
                    
                    // Only use FormData if we don't already have the value
                    if (!customerData.hasOwnProperty(key) || customerData[key] === '') {
                        customerData[key] = value;
                        console.log(`  → Using FormData value for ${key}`);
                    }
                }
            }
            console.log(`FormData entries found: ${formDataCount} customer fields, ${aspNetFieldCount} ASP.NET system fields`);
        } catch (error) {
            console.error('FormData creation failed:', error);
        }
        
        // Handle delivery address fields if different delivery is selected
        if (customerData.differentDeliveryAddress) {
            console.log('=== DELIVERY ADDRESS COLLECTION ===');
            const deliveryMappings = {
                'deliveryName': 'deliveryName',
                'deliveryLastname': 'deliveryLastname',
                'deliveryCompany': 'deliveryCompany', 
                'deliveryAddress': 'deliveryAddress',
                'deliveryAddress2': 'deliveryAddress2',
                'deliveryZip': 'deliveryZip',
                'deliveryCity': 'deliveryCity'
            };
            
            Object.entries(deliveryMappings).forEach(([fieldName, elementId]) => {
                const element = document.getElementById(elementId);
                if (element) {
                    const value = element.value ? element.value.trim() : '';
                    customerData[fieldName] = value;
                    console.log(`Delivery ${fieldName}: "${value}"`);
                }
            });
        }
        
        // CRITICAL FIX: Collect shipping option data from DOM
        console.log('=== SHIPPING OPTION COLLECTION ===');
        const selectedShippingRadio = document.querySelector('input[name="shippingOption"]:checked');
        console.log('Selected shipping radio element:', selectedShippingRadio);
        
        if (selectedShippingRadio) {
            const shippingOptionId = selectedShippingRadio.value;
            const shippingOptionName = selectedShippingRadio.dataset.name || 'Leverans';
            const shippingPrice = parseFloat(selectedShippingRadio.dataset.price) || 0;
            const shippingMethod = selectedShippingRadio.dataset.method || 'Standard';
            const shippingTaxRate = parseFloat(selectedShippingRadio.dataset.taxRate) || 25;
            
            customerData.shippingData = {
                shippingOptionId: shippingOptionId,
                shippingOptionName: shippingOptionName,
                shippingMethod: shippingMethod,
                shippingPrice: shippingPrice,
                originalShippingPrice: shippingPrice,
                shippingTaxRate: shippingTaxRate,
                isFreeShipping: false
            };
            
            console.log('✓ Shipping option collected:', customerData.shippingData);
        } else {
            console.error('✗ No shipping option selected - this should not happen if validation passed!');
            customerData.shippingData = null;
        }
        
        // Critical validation - check for empty required fields
        console.log('=== CRITICAL FIELD VALIDATION ===');
        const requiredFields = ['name', 'lastname', 'address', 'zip', 'city', 'phone', 'email'];
        let hasEmptyRequired = false;
        
        requiredFields.forEach(field => {
            const value = customerData[field] || '';
            if (!value || value.trim() === '') {
                console.error(`CRITICAL: Required field "${field}" is empty!`);
                hasEmptyRequired = true;
            } else {
                console.log(`✓ Required field "${field}": "${value}"`);
            }
        });
        
        if (hasEmptyRequired) {
            console.error('CRITICAL: Some required fields are empty - this should not happen if validation passed!');
        }
        
        console.log('=== FINAL CUSTOMER DATA ===');
        console.log(JSON.stringify(customerData, null, 2));
        console.log('=== END CUSTOMER FORM DATA COLLECTION ===');
        
        return customerData;
    }

    setButtonLoading(loading) {
        const btn = document.getElementById('place-order-btn');
        const text = document.getElementById('place-order-text');
        const loadingText = document.getElementById('place-order-loading');
        
        if (btn && text && loadingText) {
            btn.disabled = loading;
            text.style.display = loading ? 'none' : 'inline';
            loadingText.style.display = loading ? 'inline' : 'none';
            
            // Update button text with translations
            if (loading) {
                loadingText.textContent = t('checkout.processing');
            } else {
                text.textContent = t('checkout.placeOrder');
            }
        }
    }

    showError(message) {
        // Simple alert for now - could be improved with better UI
        alert(message);
    }
}

/**
 * Initialize CustomerFormHandler - simplified KISS approach
 */
function initializeCustomerFormHandler() {
    console.log('CustomerFormHandler: Attempting to initialize...');
    console.log('CustomerFormHandler: window.templateType =', typeof window.templateType !== 'undefined' ? window.templateType : 'undefined');
    
    // Simplified check: Only initialize if order-items-list exists (unique to checkout)
    const orderItemsList = document.getElementById('order-items-list');
    
    console.log('CustomerFormHandler: Checkout elements found:', {
        'order-items-list': !!orderItemsList,
        'aspnetForm': !!document.getElementById('aspnetForm')
    });
    
    if (!orderItemsList) {
        console.log('CustomerFormHandler: No order-items-list found, not a checkout page - skipping initialization');
        return;
    }
    
    console.log('CustomerFormHandler: This is a checkout page, proceeding with initialization');
    
    // Check if already initialized
    if (orderItemsList.dataset.customerFormInitialized) {
        console.log('CustomerFormHandler: Already initialized, skipping');
        return;
    }
    
    // Mark as initialized
    orderItemsList.dataset.customerFormInitialized = 'true';
    
    // Wait for ecommerce system to be ready
    if (!window.ecommerce || !window.ecommerce.config) {
        console.log('CustomerFormHandler: Waiting for ecommerce system...');
        setTimeout(() => {
            // Reset flag and retry
            delete orderItemsList.dataset.customerFormInitialized;
            initializeCustomerFormHandler();
        }, 100);
        return;
    }
    
    // Initialize the handler
    console.log('CustomerFormHandler: ECommerce system ready, initializing handler...');
    const handler = new CustomerFormHandler(window.ecommerce);
    handler.init();
}

// Multiple initialization strategies to ensure it runs
document.addEventListener('DOMContentLoaded', initializeCustomerFormHandler);

// Also listen for ecommerceReady event as backup
document.addEventListener('ecommerceReady', () => {
    console.log('CustomerFormHandler: ecommerceReady event received, attempting initialization...');
    setTimeout(initializeCustomerFormHandler, 50);
});

// Final fallback - try after a delay
setTimeout(() => {
    console.log('CustomerFormHandler: Fallback initialization attempt...');
    initializeCustomerFormHandler();
}, 1000);
